// @flow
// external libs
import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'
import { REHYDRATE } from 'redux-persist/lib/constants'

// config
import { config } from '../../Config/ConfigLoader'

// core
import { extendTemplateVariables } from '../InputHelpFunctions'
import { generateTexts } from '../SpecificationFunctions'
import { sortLimits } from '../FilterFunctions'

// types
import type { ProductState } from './Types/ProductStateType'
import type { Values } from '../Types/Values'
import type { ExpandedSpecificationDTO } from '../Types/Field'
import type { ProductContent } from '../Types/ProductContent'
import type { TabType } from '../Types/TabType'
import type { TextBlockType } from '../Types/TextTypes'
import type { ArrayFieldFormTypes } from '../Types/ArrayFieldFormType'
import type { TariffsContentType } from '../Types/TariffsContentType'
import type { DocumentType } from '../Types/DocumentTypes'
import { checkIfShouldBeDeleted } from '../ContainerFunctions'
import type QuoteInstance from '../Controllers/QuoteInstance'
import type { PriceIncludesBlacklistType } from '../Types/QuoteType'

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  allProductsRequest: null,
  allProductsRequestSuccess: ['products'],
  calculatorContentRequest: ['calculatorName', 'isBackOffice'],
  calculatorContentRequestSuccess: ['calculatorContent'],
  contractDraftContentRequest: ['calculatorName'],
  contractDraftContentRequestSuccess: ['contractDraftContent'],
  calculatorResourcesRequest: ['calculatorName'],
  calculatorCoveragesRequest: ['calculatorName'],
  calculatorSpecificationRequest: ['calculatorName'],
  calculatorSpecificationRequestSuccess: ['calculatorSpecification'],
  calculatorTextContentRequest: ['calculatorName'],
  calculatorTextContentRequestSuccess: ['step4TextOrder', 'textBlocks', 'textAlerts'],
  onChangeCalculatorSpecification: ['calculatorSpecification'],
  onChangeProductSpecification: ['specification'],
  onSetProductName: ['productName'],
  productContentRequestLoaderOff: ['product', 'isBackOffice'],
  productContentRequestSuccess: ['productContent'],
  productResourcesRequestLoaderOff: ['product'],
  productTextContentRequest: ['productName'],
  productTextContentRequestSuccess: ['step4TextOrder', 'textBlocks', 'textAlerts', 'documentStep4Order'],
  resourcesRequestSuccess: ['resources'],
  saveCalculatorValidator: ['calculatorValidator'],
  saveProductValidator: ['validator'],
  setFieldReferences: ['fieldReferences'],
  setTransManufacturer: ['transManufacturer'],
  setTransModel: ['transModel'],
  getTariffsContentRequest: ['productName'],
  getTariffsContentRequestSuccess: ['tariffsContent'],
  getOneTariffContentRequest: ['productName', 'tariffName'],
  getTwoTariffContentsRequest: ['productName', 'tariffName1', 'tariffName2'],
  quoteFiltersRequest: ['product'],
  quoteFiltersRequestSuccess: ['quoteFilters'],
  productDocumentsRequest: ['product'],
  productDocumentsRequestSuccess: ['documents'],
  setSubProductName: ['subProductName'],
  setAffiliate: ['affiliate'],
  afterRehydratation: null,
  setPriceIncludesBlacklist: ['priceIncludesBlacklist'],
  initProduct: ['pathName'],
})

export const ProductTypes = Types
export default Creators

/* ------------- Initial State ------------- */

export const INITIAL_STATE: ProductState = Immutable({
  affiliate: '',
  calculatorContent: {},
  calculatorSpecification: {},
  calculatorTexts: {},
  calculatorValidator: {},
  cicTariffsFetched: false,
  contractDraftContent: {},
  contractDraftTexts: {},
  defaultTariffsFetched: false,
  defaultTemplateVariables: extendTemplateVariables({}),
  documents: {},
  documentStep4Order: [],
  fieldReferences: {},
  priceIncludesBlacklist: {},
  productContent: config.getDefaultContent(),
  productName: config.getConfig('productName'),
  products: [],
  productTexts: {},
  quoteFilters: {},
  resources: {},
  specification: config.getDefaultSpecification(),
  step4TextOrder: [],
  subProductName: '',
  tariffsContent: {},
  textAlerts: {},
  textBlocks: {},
  transManufacturer: {},
  transModel: {},
  validator: {},
  rehydrated: false,
})

/* ------------- Reducers ------------- */

export const allProductsRequestSuccessR = (state: ProductState, { products }: { products: Array<any> }): ProductState =>
  state.merge({ products })

type GenerateTextsFromSpecificationType = {
  content: ProductContent,
  specification: ExpandedSpecificationDTO,
  key?: string,
}
export const generateTextsFromSpecification = (
  state: ProductState,
  { content, specification, key }: GenerateTextsFromSpecificationType,
): ProductState => {
  let productTexts = generateTexts(specification, content)
  const stateKey = key || 'productTexts'
  if (!productTexts) {
    productTexts = state[stateKey]
  }
  return state.set(stateKey, productTexts)
}

export const productContentRequestSuccessR = (
  state: ProductState,
  { productContent }: { productContent: ProductContent },
): ProductState =>
  generateTextsFromSpecification(state, { content: productContent, specification: state.specification }).merge({
    productContent,
  })

export const saveProductValidatorR = (state: ProductState, { validator }: ProductState): ProductState =>
  generateTextsFromSpecification(state, {
    content: state.productContent,
    specification: validator.getActualSpecification(),
  }).merge({
    validator,
    specification: validator.getActualSpecification(),
  })

export const onChangeProductSpecificationR = (state: ProductState, { specification }: ProductState): ProductState =>
  state.merge({ specification })

export const calculatorContentRequestSuccessR = (
  state: ProductState,
  { calculatorContent }: ProductState,
): ProductState =>
  generateTextsFromSpecification(state, {
    content: calculatorContent,
    specification: state.calculatorSpecification,
    key: 'calculatorTexts',
  }).merge({ calculatorContent })

export const contractDraftContentRequestSuccessR = (
  state: ProductState,
  { contractDraftContent }: ProductState,
): ProductState =>
  generateTextsFromSpecification(state, {
    content: contractDraftContent,
    specification: state.calculatorSpecification,
    key: 'contractDraftTexts',
  }).merge({ contractDraftContent })

export const saveCalculatorValidatorR = (state: ProductState, { calculatorValidator }: ProductState): ProductState =>
  generateTextsFromSpecification(state, {
    content: state.calculatorContent,
    specification: calculatorValidator.getActualSpecification(),
    key: 'calculatorTexts',
  }).merge({
    calculatorValidator,
    calculatorSpecification: calculatorValidator.getActualSpecification(),
  })

export const onChangeCalculatorSpecificationR = (
  state: ProductState,
  { calculatorSpecification }: ProductState,
): ProductState => state.merge({ calculatorSpecification })

export const resourcesRequestSuccessR = (state: ProductState, { resources }: ProductState): ProductState =>
  state.merge({ resources: Object.assign(state.resources.asMutable(), resources) })

export const setProductName = (state: ProductState, { productName }: ProductState): ProductState =>
  state.merge({ productName })

export const productTextContentRequestR = (state: ProductState): ProductState =>
  state.merge({
    step4TextOrder: INITIAL_STATE.step4TextOrder,
    textBlocks: INITIAL_STATE.textBlocks,
    textAlerts: INITIAL_STATE.textAlerts,
    documentStep4Order: INITIAL_STATE.documentStep4Order,
  })

export const processAlerts = (textAlerts: TextBlockType): TextBlockType =>
  Object.keys(textAlerts).length <= 0
    ? {}
    : Object.keys(textAlerts).reduce((acc, alertKey) => {
        const alert = textAlerts[alertKey]
        const alertNew = { type: 'info', text: '' }
        if (typeof alert === 'string') {
          alertNew.text = alert
        } else {
          if (alert.type) {
            alertNew.type = alert.type
          }
          if (alert.text) {
            alertNew.text = alert.text
          }
        }
        acc[alertKey] = alertNew
        return acc
      }, {})

export const productTextContentRequestSuccessR = (
  state: ProductState,
  { step4TextOrder, textBlocks, textAlerts, documentStep4Order }: ProductState,
): ProductState =>
  state.merge({
    step4TextOrder:
      state.step4TextOrder.length > 0 ? state.step4TextOrder : state.step4TextOrder.concat(step4TextOrder),
    textBlocks: Object.assign({}, textBlocks, state.textBlocks),
    textAlerts: Object.assign({}, processAlerts(textAlerts), state.textAlerts),
    documentStep4Order,
  })

export const calculatorTextContentRequestSuccessR = (
  state: ProductState,
  { step4TextOrder, textBlocks, textAlerts }: ProductState,
): ProductState =>
  state.merge({
    step4TextOrder: state.step4TextOrder.asMutable().concat(step4TextOrder),
    textBlocks: Object.assign(state.textBlocks.asMutable(), textBlocks),
    textAlerts: Object.assign(state.textAlerts.asMutable(), processAlerts(textAlerts)),
  })

export const setFieldReferencesR = (state: ProductState, { fieldReferences }: ProductState): ProductState =>
  state.merge({ fieldReferences })

export const setTransManufacturerR = (state: ProductState, { transManufacturer }: ProductState): ProductState =>
  state.merge({ transManufacturer })

const setTransModelR = (state: ProductState, { transModel }: ProductState): ProductState => state.merge({ transModel })

const getTariffsContentRequestSuccessR = (
  state: ProductState,
  { tariffsContent }: { tariffsContent: TariffsContentType },
): ProductState => state.set('tariffsContent', { ...state.tariffsContent, ...tariffsContent })

const quoteFiltersRequestSuccessR = (state: ProductState, { quoteFilters }: ProductState): ProductState => {
  const quoteFiltersSorted = Object.keys(quoteFilters).reduce((quoteFilterAcc, tabKey) => {
    quoteFilterAcc[tabKey] = Object.keys(quoteFilters[tabKey]).reduce((itemAcc, itemKey) => {
      itemAcc[itemKey] = quoteFilters[tabKey][itemKey]
      sortLimits(itemAcc[itemKey])
      return itemAcc
    }, {})
    return quoteFilterAcc
  }, {})
  return state.merge({ quoteFilters: quoteFiltersSorted })
}

const productDocumentsRequestR = (state: ProductState) => state.set('documents', INITIAL_STATE.documents)

const productDocumentsRequestSuccessR = (state: ProductState, { documents }: { documents: DocumentType }) =>
  state.set('documents', documents)

const setSubProductNameR = (state: ProductState, { subProductName }: { subProductName: string }) =>
  state.merge({ subProductName })

const setAffiliateR = (state: ProductState, { affiliate }: { affiliate: string }) => state.set('affiliate', affiliate)

const setPriceIncludesBlacklistR = (
  state: ProductState,
  { priceIncludesBlacklist }: { priceIncludesBlacklist: PriceIncludesBlacklistType },
) => state.set('priceIncludesBlacklist', priceIncludesBlacklist)

const afterRehydratationR = (state) => state.set('rehydrated', true)

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.ALL_PRODUCTS_REQUEST]: (state) => state,
  [Types.ALL_PRODUCTS_REQUEST_SUCCESS]: allProductsRequestSuccessR,
  [Types.PRODUCT_CONTENT_REQUEST_LOADER_OFF]: (state) => state,
  [Types.PRODUCT_CONTENT_REQUEST_SUCCESS]: productContentRequestSuccessR,
  [Types.SAVE_PRODUCT_VALIDATOR]: saveProductValidatorR,
  [Types.ON_CHANGE_PRODUCT_SPECIFICATION]: onChangeProductSpecificationR,
  [Types.CALCULATOR_CONTENT_REQUEST]: (state) => state,
  [Types.CALCULATOR_CONTENT_REQUEST_SUCCESS]: calculatorContentRequestSuccessR,
  [Types.SAVE_CALCULATOR_VALIDATOR]: saveCalculatorValidatorR,
  [Types.ON_CHANGE_CALCULATOR_SPECIFICATION]: onChangeCalculatorSpecificationR,
  [Types.PRODUCT_RESOURCES_REQUEST_LOADER_OFF]: (state) => state,
  [Types.CALCULATOR_RESOURCES_REQUEST]: (state) => state,
  [Types.CALCULATOR_COVERAGES_REQUEST]: (state) => state,
  [Types.RESOURCES_REQUEST_SUCCESS]: resourcesRequestSuccessR,
  [Types.CALCULATOR_SPECIFICATION_REQUEST]: (state) => state,
  [Types.CALCULATOR_SPECIFICATION_REQUEST_SUCCESS]: onChangeCalculatorSpecificationR,
  [Types.ON_SET_PRODUCT_NAME]: setProductName,
  [Types.PRODUCT_TEXT_CONTENT_REQUEST]: productTextContentRequestR,
  [Types.PRODUCT_TEXT_CONTENT_REQUEST_SUCCESS]: productTextContentRequestSuccessR,
  [Types.CALCULATOR_TEXT_CONTENT_REQUEST]: (state) => state,
  [Types.CALCULATOR_TEXT_CONTENT_REQUEST_SUCCESS]: calculatorTextContentRequestSuccessR,
  [Types.SET_FIELD_REFERENCES]: setFieldReferencesR,
  [Types.SET_TRANS_MANUFACTURER]: setTransManufacturerR,
  [Types.SET_TRANS_MODEL]: setTransModelR,
  [Types.GET_TARIFFS_CONTENT_REQUEST]: (state) => state,
  [Types.GET_TARIFFS_CONTENT_REQUEST_SUCCESS]: getTariffsContentRequestSuccessR,
  [Types.GET_ONE_TARIFF_CONTENT_REQUEST]: (state) => state,
  [Types.GET_TWO_TARIFF_CONTENTS_REQUEST]: (state) => state,
  [Types.QUOTE_FILTERS_REQUEST]: (state) => state,
  [Types.QUOTE_FILTERS_REQUEST_SUCCESS]: quoteFiltersRequestSuccessR,
  [Types.PRODUCT_DOCUMENTS_REQUEST]: productDocumentsRequestR,
  [Types.PRODUCT_DOCUMENTS_REQUEST_SUCCESS]: productDocumentsRequestSuccessR,
  [Types.SET_SUB_PRODUCT_NAME]: setSubProductNameR,
  [Types.SET_AFFILIATE]: setAffiliateR,
  [Types.CONTRACT_DRAFT_CONTENT_REQUEST]: (state) => state,
  [Types.CONTRACT_DRAFT_CONTENT_REQUEST_SUCCESS]: contractDraftContentRequestSuccessR,
  [Types.SET_PRICE_INCLUDES_BLACKLIST]: setPriceIncludesBlacklistR,
  [Types.INIT_PRODUCT]: (state) => state,
  [Types.AFTER_REHYDRATATION]: afterRehydratationR,
})

/* ------------- Helpers ------------- */

export const createProductDescriptorValues = (
  values: Values,
  specification: ExpandedSpecificationDTO,
  arrayTypes: ArrayFieldFormTypes,
  removeDisabled: boolean = true,
  quote?: QuoteInstance,
  checkIfShouldBeDeletedRegexp: boolean | RegExp = false,
  leaveEmptyValues: boolean = false,
  leaveEmptyObjects: boolean = false,
) => {
  const productDescriptorValues = {}
  if (values && specification && specification.fields) {
    specification.fields.forEach((el) => {
      if (
        (values[el.name] || typeof values[el.name] !== 'undefined') &&
        (el.enabled || !removeDisabled) &&
        ((typeof checkIfShouldBeDeletedRegexp === 'boolean' && !checkIfShouldBeDeletedRegexp) ||
          (checkIfShouldBeDeletedRegexp instanceof RegExp &&
            !checkIfShouldBeDeleted(
              el.name,
              values[el.name],
              typeof values[el.name] === 'object',
              checkIfShouldBeDeletedRegexp,
              leaveEmptyObjects,
            ))) &&
        (leaveEmptyValues || values[el.name] !== '')
      ) {
        if (el.domain._type.startsWith('int')) {
          productDescriptorValues[el.name] = parseInt(values[el.name], 10)
        } else if (el.domain._type === 'float') {
          productDescriptorValues[el.name] = parseFloat(values[el.name])
        } else if (el.domain._type === 'array' && Array.isArray(values[el.name])) {
          const val: Array<any> = values[el.name]
          productDescriptorValues[el.name] = arrayTypes[el.name]
            ? [...val.slice(0, arrayTypes[el.name].length)]
            : []
                // $FlowIssue
                .map((arrayValue) =>
                  createProductDescriptorValues(
                    arrayValue,
                    el.domain.specification,
                    arrayTypes,
                    removeDisabled,
                    quote,
                    checkIfShouldBeDeletedRegexp,
                    leaveEmptyValues,
                    leaveEmptyObjects,
                  ),
                )
        } else if (el.domain._type === 'object' && typeof values[el.name] === 'object') {
          // $FlowIssue
          productDescriptorValues[el.name] = createProductDescriptorValues(
            values[el.name],
            el.domain.specification,
            arrayTypes,
            removeDisabled,
            quote,
            checkIfShouldBeDeletedRegexp,
            leaveEmptyValues,
            leaveEmptyObjects,
          )
        } else {
          productDescriptorValues[el.name] = values[el.name]
        }
      }
    })
    if (!values.cicEnabled || (quote && !quote.subProductToTariffName.cic)) {
      delete productDescriptorValues.cic
    }
    if (values.cicEnabled && productDescriptorValues.cic) {
      productDescriptorValues.cicEnabled = true
    }
    return productDescriptorValues
  }
  return {}
}

export const getTabsFromSpecification = (
  specification: ExpandedSpecificationDTO,
  content: ProductContent,
): Array<TabType> => {
  const tabs = []
  specification.fields.forEach((el) => {
    if (el.domain._type === 'object' && el.enabled && el.name !== 'values' && content.fieldOrder.includes(el.name)) {
      tabs.push({ key: el.name, label: content.fieldNames[el.name], position: content.fieldOrder.indexOf(el.name) })
    }
  })
  tabs.sort((a, b) => a.position - b.position)
  return tabs
}
